import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';

interface ComplainsReportState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;


    filter: {
        dZoneGroups: any;
        targetDates: Date[];
        clients: any[];
        reason_ids: any[];
    };

    items: any[];
}

const state: ComplainsReportState = {
    loading: false,
    loaded: false,
    fatalError: false,

    filter: {
        dZoneGroups: null,
        targetDates: [new Date(), new Date()],
        clients: [],
        reason_ids: []
    },

    items: [],
};

function buildQuery (filter: any) {
    return {
      columns: {
        deal_delivery_date: 'd.expected_delivery_date',
        client_id: 'cl.id',
        client_name: 'cl.name',
        reason_name: 'cr.name',
        id: 'c.id',
        description: 'c.description',
        attachments: 'c.attachments',
      },
      from: 's:complaints',
      from_as: 'c',
      joins: [
        {
          join: 's:clients',
          as: 'cl',
          on: {
            $eq: [
              'c.client_id',
              'cl.id'
            ]
          }
        },
        {
          join: 's:deals',
          as: 'd',
          on: {
            $eq: [
              'c.deal_id',
              'd.id'
            ]
          }
        },
        {
          join: 's:complaint_complaint_reasons',
          as: 'ccr',
          on: {
            $eq: [
              'ccr.complaint_id',
              'c.id'
            ]
          }
        },
        {
          join: 's:complaint_reasons',
          as: 'cr',
          on: {
            $eq: [
              'cr.id',
              'ccr.complaint_reason_id'
            ]
          }
        }
      ],
      where: {
        $and: [
          ...(
            filter.date_from ? [
              {
                $gte: [
                  'd.expected_delivery_date',
                  {
                    $: filter.date_from
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.date_to ? [
              {
                $lte: [
                  'd.expected_delivery_date',
                  {
                    $: filter.date_to
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.clients && filter.clients.length ? [
              {
                $in: [
                  'cl.id',
                  {
                    $: filter.clients
                  }
                ]
              }
            ] : []
          ),
          ...(
            filter.reason_ids && filter.reason_ids.length ? [
              {
                $in: [
                  'cr.id',
                  {
                    $: filter.reason_ids
                  }
                ]
              }
            ] : []
          )
        ]
      },
      page_size: 100000,
      page: 1
    };
}

function buildExportQuery(filter: any) {
    return {
      template: '<!DOCTYPE html>\n<table font=Verdana font-size=8>\n  <tr>\n    <td wrap bgcolor=FF72BF44 width=10>Дата</td>\n    <td wrap bgcolor=FF72BF44 width=45>Клиент</td>\n    <td wrap bgcolor=FF72BF44 width=30>Причина</td>\n    <td wrap bgcolor=FF72BF44 width=150>Описание</td>\n  </tr>\n  {{#clients.documents}}\n    <tr>\n      <td wrap>{{deal_delivery_date}}</td>\n      <td wrap>{{client_name}}</td>\n      <td wrap>{{reason_name}}</td>\n      <td wrap>{{description}}</td>\n    </tr>\n  {{/clients.documents}}\n</table>\n',
      queries: {
        clients: buildQuery(filter)
      },
      format: 'xlsx'
    };
}

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates[0]).format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates[1]).format('YYYY-MM-DD')
    }

    if (params.dZoneGroups) {
        queryParams.delivery_zone_id = params.dZoneGroups;
    }

    if (params.clients) {
        queryParams.clients = Array.isArray(params.clients) ? params.clients : [params.clients]
    }

    if (params.reason_ids) {
        queryParams.reason_ids = Array.isArray(params.reason_ids) ? params.reason_ids : [params.reason_ids]
    }

    if (params.sortField !== null) {
        queryParams.order_by = params.sortField;
        queryParams.order_type = params.sortDirection;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';

export const actions: ActionTree<ComplainsReportState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {

            if (Object.values(params).length > 0) {
                commit('START_LOADING');

                vm.$db([
                    vm.$q(REPORT_DATA)
                        .raw('documents.list')
                        .action(buildQuery(buildFilter(params))),
                ]).then(((result: ServerResult) => {
                    commit('SET_REPORT_DATA', result.getOperation(REPORT_DATA).getContent());
                    commit('LOADED_SUCCESS');

                    resolve();
                }));
            } else {
                resolve();
            }
        })
    },

    exportDataAsXLSX({rootState}, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('EXPORT')
                .raw('documents.report')
                .action(buildExportQuery(buildFilter(params)))
            ]).then(((result: ServerResult) => {
                const exportOpContent = result.getOperation('EXPORT').getContent();
                const fToken = exportOpContent.token;
                const aToken = (rootState as any).ubic.token
                const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
                saveAs(fileUrl, 'export.xlsx')

                resolve();
            }));
        })
    },
};

export const mutations: MutationTree<ComplainsReportState> = {
    START_LOADING: (state) => {
        // state.loaded = false;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, data) {
        state.items = data.documents;
    },
};

const getters = {

};

export const STORE_KEY = '$_complains_report';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
